import { useRef, useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import AdminPage from "@/AdminPage";
import ChatBot from "@/ChatBot";
import MentionsLegales from "@/MentionsLegales";
import { BlogList, BlogArticle } from "@/BlogPage";
import SignQuotePage from "./SignQuotePage";

// Landing components
import { Navbar } from "@/components/landing/Navbar";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeaturesStrip } from "@/components/landing/FeaturesStrip";
import { ServicesSection } from "@/components/landing/ServicesSection";
import { SocialProofSection } from "@/components/landing/SocialProofSection";
import { PortfolioSection } from "@/components/landing/PortfolioSection";
import { PricingSection } from "@/components/landing/PricingSection";
// ConfiguratorSection removed per user request
import { QuoteFormSection } from "@/components/landing/QuoteFormSection";
import { FAQSection } from "@/components/landing/FAQSection";
import { ReassuranceSection } from "@/components/landing/ReassuranceSection";
import { SEOSection } from "@/components/landing/SEOSection";
import { FinalCTASection } from "@/components/landing/FinalCTASection";
import { Footer } from "@/components/landing/Footer";
import { FloatingBar } from "@/components/landing/FloatingBar";
import { WhatsAppWidget } from "@/components/landing/WhatsAppWidget";
import { ExitPopup } from "@/components/landing/ExitPopup";

// Hooks
import { useVisitorTracking } from "@/hooks/useVisitorTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function App() {
  const formRef = useRef(null);
  const [slotsData, setSlotsData] = useState({ remaining_slots: 0, total_slots: 10 });
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [dbReviews, setDbReviews] = useState([]);

  useVisitorTracking();

  const fetchSlots = async () => {
    try {
      const res = await axios.get(`${API}/slots`);
      setSlotsData(res.data);
    } catch (e) { console.error("Error fetching slots:", e); }
  };

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      if (res.data.length > 0) setDbReviews(res.data);
    } catch {}
  };

  useEffect(() => {
    fetchSlots();
    fetchReviews();
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !isSubmitted && !showExitPopup) {
        const hasSeenPopup = sessionStorage.getItem("exitPopupSeen");
        if (!hasSeenPopup) {
          setShowExitPopup(true);
          sessionStorage.setItem("exitPopupSeen", "true");
        }
      }
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [isSubmitted, showExitPopup]);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#050505] text-white font-['Outfit',sans-serif]">
      <Toaster richColors position="top-center" />

      {/* Overlays */}
      <WhatsAppWidget />
      <FloatingBar onScrollToForm={scrollToForm} />
      <ExitPopup
        show={showExitPopup}
        onClose={() => setShowExitPopup(false)}
        remainingSlots={slotsData.remaining_slots}
        onScrollToForm={scrollToForm}
      />

      {/* Navigation */}
      <Navbar onScrollToForm={scrollToForm} />

      {/* 1. Hero */}
      <HeroSection onScrollToForm={scrollToForm} />

      {/* 2. Features strip (Paclim-style guarantees row) */}
      <FeaturesStrip />

      {/* 3. Services */}
      <ServicesSection onScrollToForm={scrollToForm} />

      {/* 4. Social Proof (stats + logos + reviews) */}
      <SocialProofSection reviews={dbReviews} />

      {/* 5. Portfolio */}
      <PortfolioSection onScrollToForm={scrollToForm} />

      {/* 6. Pricing */}
      <PricingSection onScrollToForm={scrollToForm} />

      {/* 7. Quote Form */}
      <QuoteFormSection formRef={formRef} />

      {/* 9. FAQ (25 questions) */}
      <FAQSection />

      {/* 10. Reassurance */}
      <ReassuranceSection />

      {/* 11. SEO Text Block */}
      <SEOSection />

      {/* 12. Final CTA */}
      <FinalCTASection slotsData={slotsData} onScrollToForm={scrollToForm} />

      {/* 13. Footer */}
      <Footer />
    </div>
  );
}

function AppWrapper() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/devis/:quoteNumber" element={<SignQuotePage />} />
      </Routes>
      <ChatBot />
    </BrowserRouter>
  );
}

export default AppWrapper;
